import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, Search, Trash2, Plus, Minus, Printer, CheckCircle, X, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getBoutiqueVue } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { libelleVariante, getDeviseCourante } from '../../utils/format';
import IndicateurMarge from '../../components/common/IndicateurMarge';
import InputMontant from '../../components/common/InputMontant';
import DocumentImprimable, { SelecteurFormat } from '../../components/common/DocumentImprimable';
import useFormatImpression from '../../components/common/useFormatImpression';

const MODES_PAIEMENT = [
  { value: 'especes',      label: 'Espèces' },
  { value: 'orange_money', label: 'Orange Money' },
  { value: 'mtn_money',   label: 'MTN Money' },
  { value: 'autre',       label: 'Autre' },
];

function formatGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}

// ── Reçu imprimable ──────────────────────────────────────────────────────────
function RecuImprimable({ vente, onClose }) {
  const { nomCommerce, logoUrl } = useAuth();
  const nomEnt = nomCommerce || 'Mon entreprise';
  const [format, setFormat] = useFormatImpression();
  const handlePrint = () => window.print();

  // Partage WhatsApp — on demande un numéro (la vente magasin n'a pas de
  // client enregistré). Si l'utilisateur laisse vide, on ouvre wa.me sans
  // destinataire (WhatsApp Web propose alors de choisir un contact).
  const handleWhatsApp = () => {
    const tel = window.prompt(
      'Numéro WhatsApp du client (laisser vide pour choisir le contact dans WhatsApp) :',
      ''
    );
    if (tel === null) return; // annulation
    const numero = (tel || '').replace(/[^\d]/g, '');
    const lignes = (vente.lignes || []).map(l => {
      const qte = Number(l.quantite || 0);
      const pu  = Number(l.prix_unitaire || 0);
      const st  = Number(l.sous_total || qte * pu);
      const nom = [l.produit_nom, l.modele, l.taille, l.couleur && `(${l.couleur})`]
        .filter(Boolean).join(' ');
      return `• ${nom} — ${qte} × ${pu.toLocaleString('fr-FR')} = ${st.toLocaleString('fr-FR')} ${getDeviseCourante()}`;
    }).join('\n');
    const total = Number(vente.montant_total || 0).toLocaleString('fr-FR');
    const date  = new Date(vente.created_at).toLocaleString('fr-FR');
    const msg =
`*${nomEnt}*
Reçu de vente — ${date}

${lignes}

*TOTAL : ${total} ${getDeviseCourante()}*

Merci pour votre achat !`;
    const url = numero
      ? `https://wa.me/${numero}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className={`bg-white rounded-xl w-full ${format === 'a4' ? 'max-w-2xl' : 'max-w-sm'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
        {/* Barre du haut : format à gauche, fermer (croix) à droite */}
        <div className="flex items-center justify-between gap-2 px-4 pt-4 print:hidden">
          <SelecteurFormat format={format} onChange={setFormat} />
          <button onClick={onClose} title="Fermer"
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors">
            <X size={18} />
          </button>
        </div>
        {/* Actions compactes */}
        <div className="flex gap-2 px-4 pb-3 pt-3 print:hidden">
          <button onClick={handlePrint}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-sm py-2 rounded-lg transition-colors">
            <Printer size={15} /> Imprimer
          </button>
          <button onClick={handleWhatsApp} title="Partager sur WhatsApp"
            className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white font-semibold text-sm px-3 py-2 rounded-lg transition-colors">
            <MessageCircle size={15} /> WhatsApp
          </button>
        </div>

        {/* Contenu reçu (thermique ou A4 selon le choix) */}
        <div className="border-t border-gray-200">
          <DocumentImprimable
            format={format}
            titre="REÇU DE VENTE"
            sousTitre="Magasin"
            date={new Date(vente.created_at).toLocaleString('fr-FR')}
            reference={`Vente N°${vente.id}`}
            lignes={(vente.lignes || []).map(l => ({
              nom: [l.produit_nom, l.modele, l.taille, l.couleur && `(${l.couleur})`].filter(Boolean).join(' '),
              qte: Number(l.quantite || 0),
              pu: Number(l.prix_unitaire || 0),
              total: Number(l.sous_total ?? (Number(l.quantite || 0) * Number(l.prix_unitaire || 0))),
            }))}
            total={vente.montant_total}
            apresTotal={[{ label: 'Paiement', valeur: MODES_PAIEMENT.find(m => m.value === vente.mode_paiement)?.label || vente.mode_paiement }]}
            piedMessage="Merci pour votre achat !"
          />
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────
export default function VentesMagasin() {
  const [variantes, setVariantes]     = useState([]);
  const [search, setSearch]           = useState('');
  const [resultats, setResultats]     = useState([]);
  const [cartons, setCartons]         = useState([]);
  const [panier, setPanier]           = useState([]);
  const [modePaiement, setModePaiement] = useState('especes');
  const [notes, setNotes]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [recuVente, setRecuVente]     = useState(null);
  const [historique, setHistorique]   = useState([]);
  const [totaux, setTotaux]           = useState(null);
  const [boutiquesActives, setBoutiquesActives] = useState([]);
  const [seuilMarge, setSeuilMarge] = useState(10);
  const [controleActif, setControleActif] = useState(true);
  const searchRef = useRef(null);
  const { user } = useAuth();
  // Un utilisateur RATTACHÉ à une boutique précise (ex : gérant/vendeur) n'a
  // pas de sélecteur de boutique et n'a pas à en choisir : le serveur force
  // sa boutique. Seuls les admin/propriétaires (non rattachés) doivent choisir.
  const estLieBoutique = !!user?.boutique_id;
  const doitChoisirBoutique = boutiquesActives.length > 1 && !estLieBoutique && !getBoutiqueVue();

  // Charger stock + historique du jour
  useEffect(() => {
    chargerStock();
    chargerHistorique();
    api.get('/cartons').then(r => setCartons(r.data || [])).catch(() => setCartons([]));
    api.get('/parametres')
      .then(r => {
        const v = parseFloat(r.data?.marge_seuil_alerte_pct);
        if (Number.isFinite(v) && v >= 0) setSeuilMarge(v);
        setControleActif(r.data?.controle_marge_actif !== 'false');
      })
      .catch(() => {});
    // Nombre de boutiques actives — pour savoir si la vente exige un choix
    api.get('/boutiques')
      .then(r => setBoutiquesActives((r.data || []).filter(b => b.statut === 'actif')))
      .catch(() => setBoutiquesActives([]));
  }, []);

  // Recherche en temps réel
  useEffect(() => {
    if (!search.trim()) { setResultats([]); return; }
    const q = search.toLowerCase();
    // On cherche sur le NOM et les caractéristiques lisibles. Le code interne
    // (aléatoire) n'est pris en compte que pour une recherche précise (≥ 4
    // caractères), sinon une seule lettre ferait remonter des produits sans
    // rapport (ex. « s » → un code contenant un s).
    const arts = variantes.filter(v =>
      v.produit_nom?.toLowerCase().includes(q) ||
      v.modele?.toLowerCase().includes(q) ||
      v.taille?.toLowerCase().includes(q) ||
      v.couleur?.toLowerCase().includes(q) ||
      (q.length >= 4 && v.code_variante?.toLowerCase().includes(q))
    ).map(v => ({ ...v, _type: 'variante' }));
    // Pertinence : les produits dont le NOM commence par la recherche d'abord.
    arts.sort((a, b) =>
      (a.produit_nom?.toLowerCase().startsWith(q) ? 0 : 1) -
      (b.produit_nom?.toLowerCase().startsWith(q) ? 0 : 1)
    );
    // Cartons correspondants, normalisés au même format d'affichage
    const lots = cartons.filter(c =>
      c.nom?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q)
    ).map(c => ({
      _type: 'carton', carton_id: c.id,
      produit_nom: `${c.nom}`,
      quantite_disponible: c.stock_disponible,
      prix_vente_magasin: c.prix_vente_magasin,
      attributs: {},
    }));
    setResultats([...lots, ...arts].slice(0, 14));
  }, [search, variantes, cartons]);

  async function chargerStock() {
    try {
      const { data } = await api.get('/stock?limit=500');
      setVariantes(data.stock || []);
    } catch { /* silencieux */ }
  }

  async function chargerHistorique() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await api.get(`/ventes-magasin?date_debut=${today}&date_fin=${today}&limit=20`);
      setHistorique(data.ventes || []);
      setTotaux(data.totaux || null);
    } catch { /* silencieux */ }
  }

  // Ajouter au panier
  // Le dernier article ajouté (ou ré-ajouté) remonte TOUJOURS en tête de
  // liste — pratique en caisse, le caissier voit immédiatement ce qu'il
  // vient de scanner sans avoir à descendre.
  // Clé unifiée : carton OU variante
  const cleDe = (x) => x._type === 'carton' || x.carton_id ? `c-${x.carton_id}` : `v-${x.variante_id}`;

  function ajouterAuPanier(item) {
    const cle = cleDe(item);
    setPanier(prev => {
      const existant = prev.find(p => p.cle === cle);
      if (existant) {
        if (existant.quantite >= item.quantite_disponible) {
          toast.error('Stock insuffisant');
          return prev;
        }
        const autres = prev.filter(p => p.cle !== cle);
        return [{ ...existant, quantite: existant.quantite + 1 }, ...autres];
      }
      if (item.quantite_disponible <= 0) {
        toast.error('Rupture de stock');
        return prev;
      }
      return [{
        cle,
        _type: item._type === 'carton' ? 'carton' : 'variante',
        variante_id: item.variante_id || null,
        carton_id: item.carton_id || null,
        produit_nom: item.produit_nom,
        attributs: item.attributs,
        modele: item.modele, taille: item.taille, couleur: item.couleur,
        prix_unitaire: item.prix_vente_magasin || 0,
        prix_achat_moyen: item.prix_achat_moyen || 0,
        quantite: 1,
        stock_dispo: item.quantite_disponible,
      }, ...prev];
    });
    setSearch('');
    setResultats([]);
    searchRef.current?.focus();
  }

  function modifierQuantite(cle, delta) {
    setPanier(prev => prev.map(p => {
      if (p.cle !== cle) return p;
      const nv = p.quantite + delta;
      if (nv <= 0) return null;
      if (nv > p.stock_dispo) { toast.error('Stock insuffisant'); return p; }
      return { ...p, quantite: nv };
    }).filter(Boolean));
  }

  function modifierPrix(cle, val) {
    setPanier(prev => prev.map(p =>
      p.cle === cle ? { ...p, prix_unitaire: parseFloat(val) || 0 } : p
    ));
  }

  function retirerDuPanier(cle) {
    setPanier(prev => prev.filter(p => p.cle !== cle));
  }

  const totalPanier = panier.reduce((s, p) => s + p.quantite * p.prix_unitaire, 0);

  async function validerVente(confirmerPerte = false) {
    if (panier.length === 0) return toast.error('Panier vide');
    if (totalPanier <= 0) return toast.error('Montant invalide');
    // Commerce multi-boutique : on EXIGE une boutique précise (pas la vue
    // « toutes »), sinon la vente partirait par défaut sur Principale et
    // débiterait le mauvais stock. On bloque proprement.
    if (doitChoisirBoutique) {
      return toast.error('Choisis d\'abord la boutique de vente dans le bandeau en haut (pas « Toutes les boutiques »).');
    }
    setLoading(true);
    try {
      const { data } = await api.post('/ventes-magasin', {
        lignes: panier.map(p => (
          p._type === 'carton'
            ? { carton_id: p.carton_id, quantite: p.quantite, prix_unitaire: p.prix_unitaire }
            : { variante_id: p.variante_id, quantite: p.quantite, prix_unitaire: p.prix_unitaire }
        )),
        mode_paiement: modePaiement,
        notes: notes || null,
        // Vend depuis la boutique sélectionnée (sélecteur du haut). Si aucune
        // (vue « toutes »), le serveur prend la boutique par défaut du commerce.
        boutique_id: getBoutiqueVue() || undefined,
        confirmer_vente_perte: confirmerPerte,
      });
      toast.success('Vente enregistrée !');
      // Préparer reçu avec lignes du panier
      setRecuVente({ ...data, lignes: panier.map(p => ({
        ...p, sous_total: p.quantite * p.prix_unitaire
      }))});
      setPanier([]);
      setNotes('');
      chargerHistorique();
      chargerStock();
    } catch (err) {
      // ── Garde-fou rentabilité : le serveur signale une vente à perte.
      //    On affiche les articles concernés et on redemande confirmation.
      if (err.response?.status === 409 && err.response.data?.code === 'VENTE_A_PERTE' && !confirmerPerte) {
        const lignes = err.response.data.lignes || [];
        const recap = lignes
          .map(l => `• ${l.libelle || 'article'} : ${formatGNF(l.prix)} < coût ${formatGNF(l.cout)}`)
          .join('\n');
        if (window.confirm(`⚠️ Vente à perte détectée\n\n${recap}\n\nVendre quand même à ce prix ?`)) {
          return validerVente(true);
        }
        return; // annulé par le caissier
      }
      toast.error(err.response?.data?.message || 'Erreur lors de la vente');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-full flex flex-col gap-4">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white">Ventes Magasin</h1>
          <p className="text-gray-400 text-sm">Point de vente — {new Date().toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long' })}</p>
        </div>
        {totaux && (
          <div className="flex gap-3">
            <div className="bg-gray-800 rounded-xl px-4 py-2 text-center">
              <p className="text-xs text-gray-400">Ventes du jour</p>
              <p className="text-yellow-400 font-bold">{totaux.nb_ventes}</p>
            </div>
            <div className="bg-gray-800 rounded-xl px-4 py-2 text-center">
              <p className="text-xs text-gray-400">CA du jour</p>
              <p className="text-green-400 font-bold text-sm">{formatGNF(totaux.total_ca)}</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-4 flex-col lg:flex-row flex-1 min-h-0">
        {/* ── Colonne gauche : recherche + historique ── */}
        <div className="flex flex-col gap-4 w-full lg:w-1/2 min-h-0">
          {/* Recherche */}
          <div className="bg-gray-800/50 rounded-2xl border border-gray-700 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un article (nom, modèle, code, taille)…"
                className="w-full bg-gray-900 border border-gray-600 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                autoFocus
              />
            </div>

            {/* Résultats */}
            {resultats.length > 0 && (
              <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
                {resultats.map(v => (
                  <button
                    key={cleDe(v)}
                    onClick={() => ajouterAuPanier(v)}
                    disabled={v.quantite_disponible <= 0}
                    className="w-full flex items-center justify-between p-3 bg-gray-900 hover:bg-gray-700 disabled:opacity-40 rounded-xl text-left transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">
                        {v.produit_nom}{libelleVariante(v) ? ` · ${libelleVariante(v)}` : ''}
                        {v._type === 'carton' && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 align-middle">Lot</span>
                        )}
                      </p>
                      <p className="text-gray-400 text-xs">
                        {v._type === 'carton' ? `${v.quantite_disponible} lot(s) formable(s)` : `Stock: ${v.quantite_disponible}`}
                      </p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-yellow-400 font-bold text-sm">{formatGNF(v.prix_vente_magasin)}</p>
                      <Plus size={14} className="text-gray-400 ml-auto" />
                    </div>
                  </button>
                ))}
              </div>
            )}
            {search.trim() && resultats.length === 0 && (
              <p className="text-gray-500 text-sm mt-3 text-center">Aucun article trouvé</p>
            )}
          </div>

          {/* Historique du jour */}
          <div className="bg-gray-800/50 rounded-2xl border border-gray-700 p-4 flex-1 overflow-y-auto">
            <h3 className="text-sm font-bold text-gray-300 mb-3">Ventes du jour</h3>
            {historique.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">Aucune vente aujourd'hui</p>
            ) : (
              <div className="space-y-2">
                {historique.map(v => (
                  <div key={v.id} className="flex items-center justify-between p-3 bg-gray-900/60 rounded-xl">
                    <div>
                      <p className="text-white text-sm font-medium">
                        {v.lignes?.length} article{v.lignes?.length > 1 ? 's' : ''}
                        <span className="text-gray-500 ml-2 text-xs">{v.heure?.slice(0,5)}</span>
                      </p>
                      <p className="text-gray-400 text-xs">{v.vendeur_nom}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">{formatGNF(v.montant_total)}</p>
                      <p className="text-gray-500 text-xs">{MODES_PAIEMENT.find(m=>m.value===v.mode_paiement)?.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Colonne droite : panier ── */}
        <div className="flex flex-col w-full lg:w-1/2 bg-gray-800/50 rounded-2xl border border-gray-700 p-4 min-h-0">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart className="text-yellow-400" size={20} />
            <h2 className="text-lg font-bold text-white">Panier</h2>
            {panier.length > 0 && (
              <span className="ml-auto bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                {panier.reduce((s,p) => s+p.quantite, 0)} art.
              </span>
            )}
          </div>

          {/* Articles du panier */}
          <div className="flex-1 overflow-y-auto space-y-2 mb-4">
            {panier.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-600">
                <ShoppingCart size={32} className="mb-2 opacity-40" />
                <p className="text-sm">Panier vide</p>
              </div>
            ) : (
              panier.map(p => (
                <div key={p.cle} className="bg-gray-900/70 rounded-xl p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-white text-sm font-medium truncate">{p.produit_nom}{libelleVariante(p) ? ` · ${libelleVariante(p)}` : ''}</p>
                    </div>
                    <button onClick={() => retirerDuPanier(p.cle)}
                      className="text-gray-600 hover:text-red-400 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Quantité */}
                    <div className="flex items-center gap-1 bg-gray-800 rounded-lg">
                      <button onClick={() => modifierQuantite(p.cle, -1)}
                        className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-white">
                        <Minus size={16} />
                      </button>
                      <span className="text-white font-bold w-6 text-center text-sm">{p.quantite}</span>
                      <button onClick={() => modifierQuantite(p.cle, +1)}
                        className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-white">
                        <Plus size={16} />
                      </button>
                    </div>
                    {/* Prix unitaire modifiable */}
                    <div className="flex-1 relative">
                      <InputMontant
                        value={p.prix_unitaire}
                        onChange={v => modifierPrix(p.cle, v)}
                        placeholder={`0 ${getDeviseCourante()}`}
                        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-2 py-1 text-yellow-400 text-sm font-bold text-right focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                    {/* Sous-total */}
                    <span className="text-green-400 font-bold text-sm whitespace-nowrap">
                      {formatGNF(p.quantite * p.prix_unitaire)}
                    </span>
                  </div>
                  {controleActif && p._type !== 'carton' && (p.prix_unitaire > 0 || p.prix_achat_moyen > 0) && (
                    <div className="mt-1.5">
                      <IndicateurMarge prix={p.prix_unitaire} cout={p.prix_achat_moyen} seuilPct={seuilMarge} compact />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Total + paiement + validation */}
          <div className="border-t border-gray-700 pt-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total</span>
              <span className="text-2xl font-black text-yellow-400">{formatGNF(totalPanier)}</span>
            </div>

            {/* Mode paiement */}
            <div className="grid grid-cols-2 gap-2">
              {MODES_PAIEMENT.map(m => (
                <button key={m.value}
                  onClick={() => setModePaiement(m.value)}
                  className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                    modePaiement === m.value
                      ? 'bg-yellow-500 text-black font-bold'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}>
                  {m.label}
                </button>
              ))}
            </div>

            {/* Notes */}
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Notes (optionnel)"
              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-500"
            />

            {/* Avertissement : boutique non choisie en multi-boutique */}
            {doitChoisirBoutique && (
              <div className="bg-amber-500/15 border border-amber-500/50 rounded-xl px-3 py-2 text-amber-300 text-sm flex items-center gap-2">
                Choisis la boutique de vente dans le bandeau en haut avant de valider.
              </div>
            )}

            {/* Valider */}
            <button
              onClick={() => validerVente()}
              disabled={loading || panier.length === 0 || doitChoisirBoutique}
              className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <><CheckCircle size={20} /> Valider la vente</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Reçu */}
      {recuVente && <RecuImprimable vente={recuVente} onClose={() => setRecuVente(null)} />}
    </div>
  );
}
